import React from 'react';
import { useSelector } from 'react-redux';
import {
  ChargePointLog,
  useGetChargePoint,
  useListChargePointAuditLogs,
  useListChargePointLogs,
} from '../../../../../../app/ApiGen';
import { DataErrorHandler } from '../../../../../../components/ErrorHandler';
import { Loading, Section, Workspace } from '../../../../../../design-system';
import { fullStateSelector } from '../../../../../../store/root';
import { ChargePointLogs } from '../../../../../../utils/ChargePointLogs';
import { useEndDateState, useSetDateRange, useStartDateState } from '../../../../../../utils/dateRange';
import { normalise, Normalised } from '../../../../../../utils/request';
import { ChargePointLogsTable } from '../Logs/components/ChargePointLogsTable';
import { useChargePoint } from '../Provider';
import { ConnectionChart } from './components/ConnectionChart';
import { EventChart } from './components/EventChart';
import { MeteringCharts } from './components/MeteringCharts';

const ChargePointMetrics: React.VFC<{ chargePointId: string }> = ({ chargePointId }: { chargePointId: string }) => {
  const [fromDate, setFromDate] = useStartDateState();
  const [toDate, setToDate] = useEndDateState();
  const setDateRange = useSetDateRange(setFromDate, setToDate);

  const { isEvnexAdmin } = useSelector(fullStateSelector);
  const queryParams = isEvnexAdmin ? { queryParams: { from: fromDate.toISOString(), to: toDate.toISOString() } } : {};

  const {
    loading,
    error,
    data,
    refetch: refetchChargePointLogs,
  } = useListChargePointLogs({ id: chargePointId, ...queryParams });

  const {
    loading: chargePointLoading,
    data: chargePointData,
    error: chargePointError,
  } = useGetChargePoint({ id: chargePointId });

  const { data: amberLogs, refetch: refetchAuditLogs } = useListChargePointAuditLogs({
    id: chargePointId,
    ...queryParams,
  });

  const refetch = React.useCallback(async () => {
    await refetchChargePointLogs();
    await refetchAuditLogs();
  }, [refetchAuditLogs, refetchChargePointLogs]);

  if (loading || chargePointLoading) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  if (error || !data || chargePointError || !chargePointData) {
    return <DataErrorHandler description="Unable to load charge point logs" error={error} refetch={refetch} />;
  }

  const chargePointLogs: Normalised<ChargePointLog>[] = normalise(data).sort(
    (a, b) => new Date(a.serverEventDate).getTime() - new Date(b.serverEventDate).getTime(),
  );
  const chargePoint = normalise(chargePointData);

  const minXValue = chargePointLogs[0]?.serverEventDate ?? '';
  const maxXValue = chargePointLogs[chargePointLogs.length - 1]?.serverEventDate ?? '';

  return (
    <Workspace maxWidth={false}>
      <Section>
        <ChargePointLogsTable
          tableProps={{
            loading,
            refetch,
            chargePointLogs,
            exportFileName: `logs-${chargePoint.referenceId ?? chargePoint.id}`,
          }}
          dateProps={{ dateRange: { fromDate, toDate }, setDateRange }}
        />
      </Section>
      {chargePointLogs.length > 0 && (
        <Section>
          <Section style={{ paddingTop: 32 }} title="Connection Chart">
            <ConnectionChart data={chargePointLogs} startingDate={minXValue} endDate={maxXValue} />
          </Section>
          <Section style={{ paddingTop: 32 }} title="Electrical Characteristic Charts">
            <MeteringCharts
              data={new ChargePointLogs(chargePointLogs)}
              startingDate={minXValue}
              endDate={maxXValue}
              amberLogs={amberLogs}
              chargePoint={chargePoint}
            />
          </Section>
          <Section style={{ paddingTop: 32 }} title="Events Chart">
            <EventChart data={new ChargePointLogs(chargePointLogs)} startingDate={minXValue} endDate={maxXValue} />
          </Section>
        </Section>
      )}
    </Workspace>
  );
};

export const DiagnosticsView: React.VFC = () => {
  const { chargePoint, loading, error, refetch } = useChargePoint();
  if (loading) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  if (error || !chargePoint) {
    return <DataErrorHandler description="Unable to load charge point" error={error} refetch={refetch} />;
  }

  return <ChargePointMetrics chargePointId={chargePoint.id} />;
};
